/* eslint-disable no-console */

const BANNER_ID = 'msw-dev-banner';
const TIMEOUT_MS = 5000;
const MAX_HEAL_ATTEMPTS = 3;
const HEAL_ATTEMPTS_KEY = 'msw-heal-attempts';

class WorkerTimeoutError extends Error {
  constructor() {
    super(
      `Service worker stuck installing (timed out after ${
        TIMEOUT_MS / 1000
      } s)`,
    );
    this.name = 'WorkerTimeoutError';
  }
}

function showDevBanner(message: string, isError = false): void {
  document.getElementById(BANNER_ID)?.remove();
  const banner = document.createElement('div');
  banner.id = BANNER_ID;
  banner.style.cssText = [
    'position:fixed;top:0;left:0;right:0;z-index:999999',
    `background:${isError ? '#dc2626' : '#d97706'}`,
    'color:#fff;padding:10px 16px;font:13px/1.4 monospace',
    'display:flex;align-items:center;gap:10px',
  ].join(';');
  const label = document.createElement('strong');
  label.textContent = '[MSW]';
  const text = document.createElement('span');
  text.textContent = message;
  if (!isError) {
    const hint = document.createElement('small');
    hint.style.cssText = 'opacity:.75;margin-left:auto';
    hint.textContent =
      'Open DevTools → Application → Service Workers for details';
    banner.append(label, text, hint);
  } else {
    const dismiss = document.createElement('button');
    dismiss.textContent = '✕';
    dismiss.style.cssText =
      'margin-left:auto;background:transparent;border:none;color:#fff;cursor:pointer;font-size:16px';
    dismiss.onclick = () => banner.remove();
    banner.append(label, text, dismiss);
  }
  document.body.appendChild(banner);
}

function hideDevBanner(): void {
  document.getElementById(BANNER_ID)?.remove();
}

function getHealAttempts(): number {
  return parseInt(sessionStorage.getItem(HEAL_ATTEMPTS_KEY) ?? '0', 10);
}

function incrementHealAttempts(): number {
  const next = getHealAttempts() + 1;
  sessionStorage.setItem(HEAL_ATTEMPTS_KEY, String(next));
  return next;
}

function clearHealAttempts(): void {
  sessionStorage.removeItem(HEAL_ATTEMPTS_KEY);
}

async function unregisterAllServiceWorkers(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  await Promise.race([
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => {
        console.warn(`[MSW] Unregistering ${regs.length} service worker(s)`);
        return Promise.all(regs.map((r) => r.unregister()));
      })
      .then((results) => console.warn('[MSW] Unregister results:', results)),
    // Zombie SWs can cause getRegistrations/unregister to hang — don't block the reload.
    new Promise<void>((resolve) => setTimeout(resolve, 1500)),
  ]);
}

async function startWorker(): Promise<void> {
  const { worker } = await import('./dev-server');
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(
      () => reject(new WorkerTimeoutError()),
      TIMEOUT_MS,
    );
  });
  try {
    await Promise.race([
      worker.start({ onUnhandledRequest: 'bypass' as const, quiet: false }),
      timeout,
    ]);
  } finally {
    clearTimeout(timeoutHandle);
  }
}

export async function enableMocking(): Promise<void> {
  if (!import.meta.env.DEV || import.meta.env.VITE_DEV_MOCK_SERVER !== 'true') {
    return;
  }

  const allow = import.meta.env.VITE_DEV_MOCK_HANDLERS_ALLOW;
  const deny = import.meta.env.VITE_DEV_MOCK_HANDLERS_DENY;

  let scope = 'all';
  if (allow) {
    scope = `allow: ${allow}`;
  } else if (deny) {
    scope = `deny: ${deny}`;
  }

  console.log(`[MSW] Mock server enabled (${scope})`);
  showDevBanner('Starting service worker…');

  try {
    await startWorker();
    clearHealAttempts();
    hideDevBanner();
    console.log('[MSW] ✓ Service worker activated');
  } catch (firstError: unknown) {
    if (firstError instanceof WorkerTimeoutError) {
      const attempt = incrementHealAttempts();

      if (attempt > MAX_HEAL_ATTEMPTS) {
        clearHealAttempts();
        console.error(
          '[MSW] ✗ Self-heal exhausted — mocking disabled. Clear site data to reset.',
        );
        showDevBanner(
          `Self-heal failed after ${MAX_HEAL_ATTEMPTS} attempts — mocking disabled. DevTools → Application → Storage → Clear site data.`,
          true,
        );
        return;
      }

      const backoffMs = (attempt - 1) * 1000;
      console.warn(
        `[MSW] ⚠ Service worker timed out — self-healing attempt ${attempt}/${MAX_HEAL_ATTEMPTS}${
          backoffMs ? ` (waiting ${backoffMs} ms)` : ''
        }…`,
      );
      showDevBanner(
        `Service worker stuck — self-healing attempt ${attempt}/${MAX_HEAL_ATTEMPTS}…`,
      );

      if (backoffMs > 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, backoffMs));
      }

      await unregisterAllServiceWorkers();
      // MSW's worker module is cached — reloading is the only reliable way to re-register.
      showDevBanner('Reloading to complete service worker self-heal…');
      window.location.reload();
    } else {
      const message =
        firstError instanceof Error ? firstError.message : String(firstError);
      console.error('[MSW] ✗ Start failed:', message);
      showDevBanner(`Failed to start — mocking disabled. (${message})`, true);
    }
  }
}
